import { ComponentProps, useMemo, useState } from 'react'

import { LocationHour } from '@/entities/depots/models/Depot'
import { Button, Icon } from '@/shared/components'
import { pxToRem } from '@/shared/styles/themes'
import { faCopy } from '@fortawesome/pro-regular-svg-icons'
import { Box, FormHelperText, Grid, styled, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'

import { TimeEditCell } from '../GridTable/GridCells/TimeEditCell'
import { GridTable } from '../GridTable/GridTable'

export type OpeningHoursFieldProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & {
  value: LocationHour[]
  disabled?: boolean
  error?: boolean
  helperText?: string
  onChange: (value: LocationHour[]) => void
}

export const OpeningHoursField = ({
  value,
  error,
  disabled,
  helperText,
  onChange,
  ...props
}: OpeningHoursFieldProps) => {
  const columns: GridColDef[] = [
    {
      field: 'dayName',
      headerName: 'Weekday',
      width: 100,
      editable: false,
    },
    {
      field: 'openingHoursStart',
      headerName: 'Opening Time',
      width: 150,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          required
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'openingHoursEnd',
      headerName: 'Closing Time',
      width: 150,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          required
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'openingHoursEndNextDay',
      headerName: 'Next Day',
      description: 'Check if closing time extends to the next day',
      width: 100,
      editable: !disabled,
      type: 'boolean',
    },
  ]

  // Map weekday to day name
  const rows = useMemo(() => {
    if (!value) return []

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    // Move Sunday (weekday 0) to the end for display
    const sortedHours = [...value].sort((a, b) => {
      if (a.weekday === 0) return 1
      if (b.weekday === 0) return -1

      return a.weekday - b.weekday
    })

    return sortedHours.map((hour) => ({
      ...hour,
      dayName: dayNames[hour.weekday],
      isSelectable: true,
    }))
  }, [value])

  const [selectedWeekday, setSelectedWeekday] = useState<number | null>(null)
  const [copiedWeekday, setCopiedWeekday] = useState<number | null>(null)

  const handleCopyWeekday = () => {
    if (selectedWeekday !== null) {
      setCopiedWeekday(selectedWeekday)
    }
  }

  const handlePasteWeekday = () => {
    if (copiedWeekday !== null && selectedWeekday !== null && copiedWeekday !== selectedWeekday) {
      const newLocationHours = value.map(hour => {
        if (hour.weekday === selectedWeekday) {
          const sourceHourData = value.find(h => h.weekday === copiedWeekday)

          if (sourceHourData) {
            return {
              ...sourceHourData,
              id: hour.id,
              weekday: hour.weekday,
            }
          }
        }

        return hour
      })

      onChange(newLocationHours)
    }
  }

  return (
    <StyledContainer {...props}>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              fontSize: pxToRem(16),
              fontWeight: 500,
              mt: 1,
              mb: 1,
            }}
          >
            Opening Hours
          </Typography>
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              text
              round
              disabled={disabled || selectedWeekday === null}
              startIcon={<Icon icon={faCopy} />}
              onClick={handleCopyWeekday}
            >
              Copy weekday
            </Button>
            <Button
              text
              round
              disabled={disabled || selectedWeekday === null || copiedWeekday === null}
              startIcon={<Icon icon={faCopy} />}
              onClick={handlePasteWeekday}
            >
              Paste weekday
            </Button>
          </Box>
        </Grid>
      </Grid>

      <StyledGridTable
        columns={columns}
        rows={rows}
        getRowId={(row) => row.weekday}
        onRowSelectionModelChange={(model) => {
          setSelectedWeekday(model.length > 0 ? model[0] as number : null)
        }}
        processRowUpdate={(newRow) => {
          const newLocationHours = value.map(hour => {
            if (hour.weekday === newRow.weekday) {
              return {
                ...hour,
                ...newRow,
              }
            }

            return hour
          })

          onChange(newLocationHours)

          return newRow
        }}
      />
      <StyledHelperText error={error}>
        {helperText && helperText}
        {error ? 'Please ensure all opening hours are valid' : (!disabled ? 'Double-click cell to update value' : '')}
      </StyledHelperText>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledHelperText = styled(FormHelperText)`
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`

const StyledGridTable = styled(GridTable)`
  min-height: ${(props) => props.theme.typography.pxToRem(300)};
`